import {
  addMonths,
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfMonth,
  format,
  max,
  min,
  startOfMonth,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import type { Allocation } from '../types/Allocation';
import { parseApiDate, toApiDate } from './data';

/** Janela padrão quando o usuário não escolhe um range (3/6/12 meses) nem Início/Fim. */
export const DEFAULT_WINDOW_MONTHS = 3;

export type TimelineWindow = { start: Date; end: Date };

/**
 * Janela visível a partir de Início/Fim dos filtros e do range escolhido
 * (`months`). Com só um dos dois, a janela de `months` meses parte dele — nunca
 * fica invertida (início > fim). Com os dois, o range não vale (a janela é
 * exatamente o período filtrado).
 */
export function resolveWindow(
  start: Date | null,
  end: Date | null,
  today: Date,
  months: number = DEFAULT_WINDOW_MONTHS,
): TimelineWindow {
  if (start && end) return { start, end };
  if (start) return { start, end: endOfMonth(addMonths(start, months - 1)) };
  if (end) return { start: startOfMonth(addMonths(end, -(months - 1))), end };
  return { start: startOfMonth(today), end: endOfMonth(addMonths(today, months - 1)) };
}

export function windowToApiDates(window: TimelineWindow): { startDate: string; endDate: string } {
  return {
    // biome-ignore lint/style/noNonNullAssertion: `toApiDate` só retorna null pra input null/undefined
    startDate: toApiDate(window.start)!,
    // biome-ignore lint/style/noNonNullAssertion: idem
    endDate: toApiDate(window.end)!,
  };
}

function totalDays(window: TimelineWindow): number {
  return differenceInCalendarDays(window.end, window.start) + 1;
}

/** Posição em % de um intervalo dentro da janela — já recortado (clip) em quem chama. */
function rangePercent(
  start: Date,
  end: Date,
  window: TimelineWindow,
): { leftPct: number; widthPct: number } {
  const days = totalDays(window);
  const leftDays = differenceInCalendarDays(start, window.start);
  const spanDays = differenceInCalendarDays(end, start) + 1;
  return { leftPct: (leftDays / days) * 100, widthPct: (spanDays / days) * 100 };
}

export type AllocationBar = {
  allocation: Allocation;
  lane: number;
  leftPct: number;
  widthPct: number;
};

/**
 * Recorta cada alocação à janela visível e distribui em "lanes" (a mesma
 * ideia de um calendário com eventos sobrepostos): duas alocações do mesmo
 * profissional que se sobrepõem no tempo (comum — ex. 50%/50% em dois
 * projetos) vão para lanes diferentes; sem sobreposição, reaproveita a lane
 * mais baixa livre. Alocações fora da janela (não deveria acontecer — o
 * backend já filtra por sobreposição de período — mas defensivo) são
 * descartadas.
 */
export function layoutAllocations(
  allocations: Allocation[],
  window: TimelineWindow,
): AllocationBar[] {
  const clipped = allocations
    .map((allocation) => {
      const start = parseApiDate(allocation.startDate);
      const end = allocation.endDate ? parseApiDate(allocation.endDate) : window.end;
      if (!start || !end) return null;
      const clippedStart = max([start, window.start]);
      const clippedEnd = min([end, window.end]);
      if (clippedStart > clippedEnd) return null;
      return { allocation, start: clippedStart, end: clippedEnd };
    })
    .filter((x): x is { allocation: Allocation; start: Date; end: Date } => x !== null)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const laneEnds: Date[] = [];
  return clipped.map(({ allocation, start, end }) => {
    let lane = laneEnds.findIndex((laneEnd) => laneEnd < start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    return { allocation, lane, ...rangePercent(start, end, window) };
  });
}

/**
 * Pico de utilização do profissional na janela: maior soma de % simultânea em
 * qualquer ponto (pode passar de 100 = sobrealocado). Varredura (sweep-line) em
 * índice de dia inteiro a partir de `window.start` — imune a horário de verão.
 * Vem das alocações reais (não das barras, que já fundem sobreposições). Como a
 * paginação é por profissional, descreve só a página visível.
 */
export function windowPeakUtilization(allocations: Allocation[], window: TimelineWindow): number {
  const span = totalDays(window);
  const deltas = new Map<number, number>();
  for (const allocation of allocations) {
    const start = parseApiDate(allocation.startDate);
    const end = allocation.endDate ? parseApiDate(allocation.endDate) : window.end;
    if (!start || !end) continue;
    const startDay = Math.max(0, differenceInCalendarDays(start, window.start));
    const endDay = Math.min(span - 1, differenceInCalendarDays(end, window.start));
    if (startDay > endDay) continue;
    deltas.set(startDay, (deltas.get(startDay) ?? 0) + allocation.allocatedPercentage);
    deltas.set(endDay + 1, (deltas.get(endDay + 1) ?? 0) - allocation.allocatedPercentage);
  }

  let running = 0;
  let peak = 0;
  for (const day of [...deltas.keys()].sort((a, b) => a - b)) {
    running += deltas.get(day) ?? 0;
    if (running > peak) peak = running;
  }
  return peak;
}

export type MonthSegment = {
  key: string;
  label: string;
  shortLabel: string;
  year: number;
  /** Primeiro mês da janela ou janeiro — onde o cabeçalho mostra o ano, sem repetir. */
  firstOfYear: boolean;
  leftPct: number;
  widthPct: number;
};

/** Cabeçalho do Gantt: um segmento por mês (parcial nas pontas), largura proporcional aos dias dentro da janela. */
export function monthSegments(window: TimelineWindow, locale: string): MonthSegment[] {
  const months = eachMonthOfInterval({ start: window.start, end: window.end });
  const dateLocale = locale === 'en-US' ? enUS : ptBR;
  return months.map((monthStart, index) => {
    const segmentStart = max([monthStart, window.start]);
    const segmentEnd = min([endOfMonth(monthStart), window.end]);
    return {
      key: format(monthStart, 'yyyy-MM'),
      label: format(monthStart, 'MMM yyyy', { locale: dateLocale }),
      shortLabel: format(monthStart, 'MMM', { locale: dateLocale }),
      year: monthStart.getFullYear(),
      firstOfYear: index === 0 || monthStart.getMonth() === 0,
      ...rangePercent(segmentStart, segmentEnd, window),
    };
  });
}

/** `null` quando hoje cai fora da janela visível — não desenha o marcador. */
export function todayMarkerPct(today: Date, window: TimelineWindow): number | null {
  if (today < window.start || today > window.end) return null;
  return rangePercent(today, today, window).leftPct;
}
